import React, { useEffect, useState } from 'react';
import { Loader2, ShoppingBag, CreditCard, Search, Send, MessageCircle, Mail } from 'lucide-react';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { ADMIN_NAV } from '../../constants/nav';
import { adminListOrders, adminListPayments } from '../../features/orders/api/orders';
import { formatPrice } from '../../features/courses/api/courses';
import type { Order, Payment } from '../../types';
import { StatusBadge } from '../../components/common/StatusBadge';
import { FollowUpModal } from '../../components/orders/FollowUpModal';
import { ORDER } from '../../constants/testIds';

const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('id-ID') : '—');
const STATUS_OPTS = ['all', 'pending', 'paid', 'failed', 'expired'];
const inp = 'bg-[#050914] border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder:text-white/30 focus:border-cyan-400 outline-none';

export default function AdminOrdersPage() {
  const [tab, setTab] = useState<'orders' | 'payments'>('orders');
  const [orders, setOrders] = useState<Order[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [followTargets, setFollowTargets] = useState<Order[] | null>(null);

  const filteredOrders = orders.filter((o) =>
    (statusFilter === 'all' || o.status === statusFilter) &&
    (q === '' || (o.product_title || '').toLowerCase().includes(q.toLowerCase()) || o.order_code.toLowerCase().includes(q.toLowerCase()))
  );

  const toggle = (id: string) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allShownSelected = filteredOrders.length > 0 && filteredOrders.every((o) => selected.has(o.id));
  const toggleAll = () => setSelected((prev) => {
    if (allShownSelected) return new Set();
    return new Set(filteredOrders.map((o) => o.id));
  });
  const selectedOrders = orders.filter((o) => selected.has(o.id));
  const reload = () => { setSelected(new Set()); adminListOrders().then(setOrders); };

  useEffect(() => {
    Promise.all([adminListOrders(), adminListPayments()])
      .then(([o, p]) => {
        setOrders(o);
        setPayments(p);
      })
      .finally(() => setLoading(false));
  }, []);

  const tabBtn = (key: 'orders' | 'payments', label: string, Icon: any) => (
    <button
      onClick={() => setTab(key)}
      data-testid={`admin-orders-tab-${key}`}
      className={
        tab === key
          ? 'flex items-center gap-2 px-4 py-2 rounded-lg text-cyan-400 bg-cyan-400/10 border border-cyan-400/20 text-sm font-medium'
          : 'flex items-center gap-2 px-4 py-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5 text-sm transition-colors'
      }
    >
      <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
    </button>
  );

  return (
    <DashboardLayout navItems={ADMIN_NAV} title="Orders & Payments">
      <div className="max-w-5xl" data-testid={ORDER.adminPage}>
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-1">Monitoring</p>
          <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Orders & Payments</h2>
        </div>

        <div className="flex gap-2 mb-5 items-center flex-wrap">
          {tabBtn('orders', `Orders (${orders.length})`, ShoppingBag)}
          {tabBtn('payments', `Payments (${payments.length})`, CreditCard)}
          {tab === 'orders' && (
            <div className="flex gap-2 items-center ml-auto">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-white/30 absolute left-2.5 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
                <input data-testid="admin-order-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari..." className={`${inp} pl-8 w-40`} />
              </div>
              <select data-testid="admin-order-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={inp}>
                {STATUS_OPTS.map((s) => <option key={s} value={s}>{s === 'all' ? 'Semua Status' : s}</option>)}
              </select>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} />
          </div>
        ) : tab === 'orders' ? (
          filteredOrders.length === 0 ? (
            <p className="text-white/40 text-sm bg-white/[0.02] border border-white/10 rounded-xl p-6">Tidak ada order.</p>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <label className="flex items-center gap-2 text-sm text-white/60 cursor-pointer">
                  <input data-testid="admin-order-select-all" type="checkbox" checked={allShownSelected} onChange={toggleAll} className="accent-cyan-400 w-4 h-4" />
                  Pilih semua ({filteredOrders.length})
                </label>
                {selected.size > 0 && (
                  <button data-testid="admin-order-bulk-followup" onClick={() => setFollowTargets(selectedOrders)}
                    className="ml-auto flex items-center gap-2 bg-cyan-400 text-[#050914] font-medium px-4 py-2 rounded-lg hover:bg-cyan-300 transition-colors text-sm">
                    <Send className="w-4 h-4" strokeWidth={2} /> Follow Up ({selected.size})
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {filteredOrders.map((o) => (
                  <div key={o.id} data-testid={ORDER.adminOrderRow} className={`flex items-center gap-3 sm:gap-4 bg-white/[0.02] border rounded-xl p-4 transition-colors ${selected.has(o.id) ? 'border-cyan-400/40' : 'border-white/10'}`}>
                    <input data-testid={`admin-order-select-${o.id}`} type="checkbox" checked={selected.has(o.id)} onChange={() => toggle(o.id)} className="accent-cyan-400 w-4 h-4 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{o.product_title || o.product_type}</p>
                      <p className="text-xs text-white/40 font-mono truncate">{o.order_code} · {fmtDate(o.created_at)}</p>
                      {o.member && (
                        <p className="text-xs text-white/50 mt-0.5 flex items-center gap-2 flex-wrap">
                          <span className="text-white/70">{o.member.full_name || 'Member'}</span>
                          {o.member.email && <span className="inline-flex items-center gap-1 text-white/40"><Mail className="w-3 h-3" strokeWidth={1.5} />{o.member.email}</span>}
                          {o.member.phone && <span className="inline-flex items-center gap-1 text-emerald-400/70"><MessageCircle className="w-3 h-3" strokeWidth={1.5} />{o.member.phone}</span>}
                          {!!o.followup_count && <span className="text-cyan-400/70">· {o.followup_count}× follow-up</span>}
                        </p>
                      )}
                    </div>
                    <span className="text-cyan-400 font-medium text-sm whitespace-nowrap">{formatPrice(o.amount, o.currency)}</span>
                    <StatusBadge status={o.status} />
                    <button data-testid={`admin-order-followup-${o.id}`} onClick={() => setFollowTargets([o])} title="Follow Up"
                      className="shrink-0 p-2 rounded-lg text-white/60 hover:text-cyan-400 hover:bg-white/5 transition-colors">
                      <Send className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )
        ) : payments.length === 0 ? (
          <p className="text-white/40 text-sm bg-white/[0.02] border border-white/10 rounded-xl p-6">Belum ada pembayaran.</p>
        ) : (
          <div className="space-y-2">
            {payments.map((p) => (
              <div key={p.id} data-testid={ORDER.adminPaymentRow} className="flex items-center gap-4 bg-white/[0.02] border border-white/10 rounded-xl p-4">
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium capitalize">{p.gateway} · {p.payment_method}</p>
                  <p className="text-xs text-white/40 font-mono truncate">{p.gateway_reference}</p>
                </div>
                <span className="text-cyan-400 font-medium text-sm">{formatPrice(p.amount, p.currency)}</span>
                <StatusBadge status={p.payment_status} />
              </div>
            ))}
          </div>
        )}
      </div>
      {followTargets && (
        <FollowUpModal orders={followTargets} onClose={() => { setFollowTargets(null); reload(); }} />
      )}
    </DashboardLayout>
  );
}
